import axiosInstance from './axiosInstance';
import {
  BranchPurchaseRequest,
  ConsolidatedRequests,
  DailyItemPrice,
} from '../types/branchPurchaseRequests.types';

const BASE = '/BranchPurchaseRequests';

const branchPurchaseRequestsService = {
  getAll(branchId?: number, custodyId?: number, status?: string): Promise<BranchPurchaseRequest[]> {
    const params: Record<string, unknown> = {};
    if (branchId !== undefined) params.branchId = branchId;
    if (custodyId !== undefined) params.custodyId = custodyId;
    if (status !== undefined) params.status = status;
    return axiosInstance.get<BranchPurchaseRequest[]>(BASE, { params }).then((r) => r.data);
  },

  getById(id: number): Promise<BranchPurchaseRequest> {
    return axiosInstance.get<BranchPurchaseRequest>(`${BASE}/${id}`).then((r) => r.data);
  },

  create(dto: unknown): Promise<BranchPurchaseRequest> {
    return axiosInstance.post<BranchPurchaseRequest>(BASE, dto).then((r) => r.data);
  },

  update(id: number, dto: unknown): Promise<BranchPurchaseRequest> {
    return axiosInstance.put<BranchPurchaseRequest>(`${BASE}/${id}`, dto).then((r) => r.data);
  },

  delete(id: number): Promise<void> {
    return axiosInstance.delete(`${BASE}/${id}`).then(() => undefined);
  },

  submit(id: number): Promise<BranchPurchaseRequest> {
    return axiosInstance.post<BranchPurchaseRequest>(`${BASE}/${id}/submit`).then((r) => r.data);
  },

  approve(id: number): Promise<BranchPurchaseRequest> {
    return axiosInstance.post<BranchPurchaseRequest>(`${BASE}/${id}/approve`).then((r) => r.data);
  },

  reject(id: number, reason: string): Promise<BranchPurchaseRequest> {
    return axiosInstance
      .post<BranchPurchaseRequest>(`${BASE}/${id}/reject`, { reason })
      .then((r) => r.data);
  },

  recall(id: number): Promise<BranchPurchaseRequest> {
    return axiosInstance.post<BranchPurchaseRequest>(`${BASE}/${id}/recall`).then((r) => r.data);
  },

  unfulfill(id: number): Promise<BranchPurchaseRequest> {
    return axiosInstance.post<BranchPurchaseRequest>(`${BASE}/${id}/unfulfill`).then((r) => r.data);
  },

  getConsolidated(custodyId: number, fromDate?: string, toDate?: string): Promise<ConsolidatedRequests> {
    const params: Record<string, unknown> = { custodyId };
    if (fromDate !== undefined) params.fromDate = fromDate;
    if (toDate !== undefined) params.toDate = toDate;
    return axiosInstance
      .get<ConsolidatedRequests>(`${BASE}/consolidated`, { params })
      .then((r) => r.data);
  },

  getDailyPrices(custodyId: number, fromDate: string, toDate?: string): Promise<DailyItemPrice[]> {
    const params: Record<string, unknown> = { custodyId, date: fromDate };
    return axiosInstance
      .get<DailyItemPrice[]>(`${BASE}/daily-prices`, { params })
      .then((r) => r.data);
  },

  // ✅ NEW: range endpoint — merges priced items + items from requests in the range
  getDailyPricesRange(custodyId: number, fromDate: string, toDate: string): Promise<DailyItemPrice[]> {
    const params: Record<string, unknown> = { custodyId, fromDate, toDate };
    return axiosInstance
      .get<DailyItemPrice[]>(`${BASE}/daily-prices-range`, { params })
      .then((r) => r.data);
  },

  // ✅ NEW: all daily prices for a custody (with full date metadata)
  getAllDailyPrices(custodyId: number): Promise<DailyItemPrice[]> {
    return axiosInstance
      .get<DailyItemPrice[]>(`${BASE}/daily-prices/all`, { params: { custodyId } })
      .then((r) => r.data);
  },

  setDailyPrice(dto: unknown): Promise<DailyItemPrice> {
    return axiosInstance.post<DailyItemPrice>(`${BASE}/daily-prices`, dto).then((r) => r.data);
  },

  applyPricing(dto: unknown): Promise<void> {
    return axiosInstance.post(`${BASE}/apply-pricing`, dto).then(() => undefined);
  },

  convertToOrders(custodyId: number, requestIds: number[]): Promise<unknown> {
    return axiosInstance
      .post(`${BASE}/convert-to-orders`, { custodyId, requestIds })
      .then((r) => r.data);
  },

  convertToInvoices(custodyId: number, purchaseOrderIds: number[]): Promise<unknown> {
    return axiosInstance
      .post(`${BASE}/convert-to-invoices`, { custodyId, purchaseOrderIds })
      .then((r) => r.data);
  },

  getSurplusInvoices(custodyId: number, fromDate?: string, toDate?: string): Promise<any[]> {
    const params: Record<string, unknown> = { custodyId };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    return axiosInstance.get(`${BASE}/surplus-invoices`, { params }).then((r) => r.data);
  },
};

export default branchPurchaseRequestsService;
